/*
 * Global layout grid — JS access.
 *
 * The config lives in wwwroot/css/themes/grid.css; this module reads the resolved values from CSS,
 * so JS and CSS always agree (including per-breakpoint changes).
 *
 *   import { getGrid, spanWidth, onGridChange } from "./grid.js";
 *   window.siteGrid.get()              // same API on window
 *   Alpine: $store.grid.columns        // reactive, updates on resize
 *
 * Ctrl + Alt + G toggles a column overlay (state is remembered in localStorage).
 */

const STORAGE_KEY = "grid-overlay";
const listeners = new Set();
let current = null;
let overlay = null;

function readLength(name) {
    // Resolve the custom property to pixels by letting the browser compute it.
    const probe = document.createElement("div");
    probe.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;width:var(${name});`;
    document.body.appendChild(probe);
    const px = probe.getBoundingClientRect().width;
    probe.remove();
    return px;
}

function measure() {
    const styles = getComputedStyle(document.documentElement);
    const columns = parseInt(styles.getPropertyValue("--grid-columns"), 10) || 1;
    const gutter = readLength("--grid-gutter");
    const margin = readLength("--grid-margin");
    const maxWidth = readLength("--grid-max-width");
    const viewport = document.documentElement.clientWidth;
    const containerWidth = Math.min(viewport, maxWidth);
    const contentWidth = containerWidth - 2 * margin;
    const columnWidth = (contentWidth - (columns - 1) * gutter) / columns;

    return {
        columns,
        gutter,
        margin,
        maxWidth,
        viewport,
        containerWidth,
        contentWidth,
        columnWidth,
        offsetLeft: (viewport - containerWidth) / 2 + margin,
    };
}

/** Current grid values in px (columns is a count). */
export function getGrid() {
    if (!current) current = measure();
    return current;
}

/** Width in px of `n` columns including the gutters between them. */
export function spanWidth(n, grid = getGrid()) {
    return n * grid.columnWidth + Math.max(n - 1, 0) * grid.gutter;
}

/** Distance in px from the content edge to the start of column `n` (0-based). */
export function columnOffset(n, grid = getGrid()) {
    return n * (grid.columnWidth + grid.gutter);
}

/** Calls `callback(grid)` whenever the grid values change. Returns an unsubscribe function. */
export function onGridChange(callback) {
    listeners.add(callback);
    return () => listeners.delete(callback);
}

function renderOverlay() {
    if (!overlay) return;
    const inner = overlay.firstElementChild;
    const { columns } = getGrid();
    if (inner.childElementCount === columns) return;
    inner.replaceChildren(
        ...Array.from({ length: columns }, () => {
            const column = document.createElement("div");
            column.className = "grid-overlay__column";
            return column;
        })
    );
}

export function showGridOverlay() {
    if (overlay) return;
    overlay = document.createElement("div");
    overlay.className = "grid-overlay";
    overlay.setAttribute("aria-hidden", "true");
    overlay.appendChild(document.createElement("div")).className = "grid-overlay__inner";
    document.body.appendChild(overlay);
    renderOverlay();
    localStorage.setItem(STORAGE_KEY, "1");
}

export function hideGridOverlay() {
    overlay?.remove();
    overlay = null;
    localStorage.removeItem(STORAGE_KEY);
}

export function toggleGridOverlay() {
    overlay ? hideGridOverlay() : showGridOverlay();
}

let lastWidth = -1;

function update() {
    const width = document.documentElement.clientWidth;
    if (width === lastWidth) return;
    lastWidth = width;
    document.documentElement.style.setProperty("--grid-viewport", `${width}px`);
    const next = measure();
    const changed = !current || Object.keys(next).some((key) => next[key] !== current[key]);
    current = next;
    if (!changed) return;
    renderOverlay();
    listeners.forEach((callback) => callback(current));
}

function init() {
    update();
    new ResizeObserver(update).observe(document.documentElement);

    document.addEventListener("keydown", (event) => {
        if (event.ctrlKey && event.altKey && event.code === "KeyG") {
            event.preventDefault();
            toggleGridOverlay();
        }
    });

    if (localStorage.getItem(STORAGE_KEY)) showGridOverlay();
}

document.addEventListener("alpine:init", () => {
    window.Alpine.store("grid", { ...getGrid() });
    onGridChange((grid) => Object.assign(window.Alpine.store("grid"), grid));
});

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}

window.siteGrid = {
    get: getGrid,
    spanWidth,
    columnOffset,
    onChange: onGridChange,
    toggleOverlay: toggleGridOverlay,
};
